import React from "react";
import { CircleNotch } from "@phosphor-icons/react";
import { Tooltip } from "@mui/material";
import { X, Sparkle } from "lucide-react";
import useDocumentMetadata from "./services/useDocumentMetadata";
import DocumentVersionsSection from "./DocumentVersionsSection";
import type {
  DocumentMetadata,
  LinkedEntity,
  SourceEmail,
} from "./services/useDocumentMetadata";
import openFile from "@/components/FpAppV4/services/openFile";
import { displayFileName } from "@/utils/fileName";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";
import {
  formatCalendarDay,
  formatInstant,
  formatTemporal,
  isTemporal,
} from "@/utils/temporal";
import { useDocumentColumns } from "../FpPages/RMS/Data/documentColumns";
import type { DocumentColumn } from "../FpPages/RMS/Data/documentColumns";
import { entityKindMap } from "../FpPages/RMS/Dashboard/utils";
import type { EntityKind } from "../FpPages/RMS/Dashboard/types";
import { createEntitySlug } from "../FpPages/RMS/Dashboard/utils/slugUtils";

// The order the linked-record rows appear in, so a document's panel reads the
// same way from one document to the next no matter what order the API returned
// the links in (#11238).
const linkedKindOrder = [
  "manager",
  "vehicle",
  "company",
  "person",
  "portfolio",
  "client",
  "deal",
  "topic",
  "interaction",
];

// A day the document states -- its document date, or a `date` column the
// organization configured. It carries no zone, so it reads the same for every
// viewer on earth and never reaches the Date constructor (#11055).
function formatDay(day: string | null | undefined): string | null {
  if (!day) return null;
  return formatCalendarDay(day, "medium");
}

// A moment -- the upload time. Its calendar day genuinely depends on who is
// reading, so it renders in the viewer's zone.
function formatUploadedAt(
  at: string | null | undefined,
  viewerZone: string
): string | null {
  if (!at) return null;
  return formatInstant(at, viewerZone, "medium");
}

// Names come from `entityKindMap`, the app's single definition of its record
// vocabulary, so this panel and the RMS pages cannot disagree on a kind's name.
function kindLabel(kind: string, count: number): string {
  const labels = entityKindMap[kind as EntityKind];
  if (!labels) return count === 1 ? kind : `${kind}s`;
  return count === 1 ? labels.label : labels.displayLabel;
}

function openEntityPreview(entity: LinkedEntity): void {
  window.dispatchEvent(
    new CustomEvent("openEntityPreview", {
      detail: {
        entityKind: entity.kind,
        entityId: entity.public_id,
        entityName: entity.name,
      },
    })
  );
}

// The app's canonical entity URL. Built with the same helper `RmsEntityPreview`
// and the note viewer use, so all three produce identical links; the entity page
// strips the trailing id back out of the slug to resolve the record.
function entityHref(entity: LinkedEntity): string {
  const slug = createEntitySlug({ name: entity.name, id: entity.public_id });
  return `/rms/${entity.kind}/${slug}`;
}

function formatDocumentTypes(
  docTypes: string[] | null | undefined
): string | null {
  // The backend writes the sentinel "empty" as the only type of a file it does
  // not LLM-process (empty / unsupported blob_kinds). That sentinel is not a
  // real type, so treat it as absent and let the Type row hide entirely.
  const real = (docTypes ?? []).filter((t) => t && t !== "empty");
  if (!real.length) return null;
  return real
    .map((t) => t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(", ");
}

function trimSummary(summary: string): string {
  const match = summary.match(/Fund: |Organization: /);
  if (match && match.index !== undefined) {
    return summary.slice(0, match.index).trim();
  }
  return summary;
}

function MetadataField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  if (value === null || value === undefined) return null;
  return (
    <div className="border-b border-gray-100 pb-2 last:border-b-0">
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  );
}

function EntityChip({
  name,
  kind,
  publicId,
}: {
  name: string;
  kind: string;
  publicId: string;
}) {
  return (
    <Tooltip title={kindLabel(kind, 1)} arrow placement="top">
      <button
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer transition-colors"
        onClick={() => openEntityPreview({ kind, public_id: publicId, name })}
      >
        {name}
      </button>
    </Tooltip>
  );
}

// Linked records read as a normal field value rather than as chips, but each
// name opens that record — so the underline is present at rest, not on hover
// (rules/frontend.md). Several names of one kind read as one comma-separated
// list that wraps onto as many lines as it needs, which is what every other
// multi-value field in this panel does (#11272).
//
// Real anchors, not buttons: a genuine href is what makes ctrl/cmd-click,
// middle-click, and "Open in new tab" work. A plain left-click is intercepted so
// it still opens the preview beside the document instead of navigating away; a
// modified click is left entirely to the browser (#11238).
function LinkedRecordValue({ entities }: { entities: LinkedEntity[] }) {
  return (
    <span>
      {entities.map((entity, i) => (
        <React.Fragment key={`${entity.kind}-${entity.public_id}`}>
          {i > 0 && <span className="text-gray-400">, </span>}
          <a
            href={entityHref(entity)}
            className="underline decoration-gray-300 underline-offset-2 hover:decoration-gray-900 hover:text-black cursor-pointer transition-colors"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              if (e.button !== 0) return;
              e.preventDefault();
              openEntityPreview(entity);
            }}
          >
            {entity.name}
          </a>
        </React.Fragment>
      ))}
    </span>
  );
}

// The emails the document arrived with (#15043), newest first, one per line,
// each named by its subject. A link like a linked record's: a real anchor to
// the email's own address, so a modified click opens it in a new tab, while a
// plain left-click opens it in the viewer the way the Versions section opens
// another version, and tells the host.
function SourceEmailValue({
  emails,
  onOpen,
}: {
  emails: SourceEmail[];
  onOpen?: () => void;
}) {
  return (
    <span className="flex flex-col gap-0.5">
      {emails.map((email) => {
        // An email with no subject has an empty title.
        const name = email.title || displayFileName(email.filename);
        return (
          <a
            key={email.blob_id}
            // The Documents page reads `docId` from any address
            // (`documentPageHref`).
            href={`/documents?docId=${encodeURIComponent(email.blob_id)}`}
            className="break-words underline decoration-gray-300 underline-offset-2 hover:decoration-gray-900 hover:text-black cursor-pointer transition-colors"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              openFile({
                fileId: email.blob_id,
                fileName: email.filename,
                fileType: "email",
              });
              onOpen?.();
            }}
          >
            {name}
          </a>
        );
      })}
    </span>
  );
}

function CustomAttributeValue({
  value,
  column,
}: {
  value: unknown;
  column: DocumentColumn | undefined;
}) {
  if (value === null || value === undefined) return null;

  // Entity references (array of {id, kind, name})
  if (Array.isArray(value) && value.length > 0 && value[0]?.kind) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.map(
          (entity: { id: string; kind: string; name: string }, i: number) => (
            <EntityChip
              key={i}
              name={entity.name}
              kind={entity.kind}
              publicId={entity.id}
            />
          )
        )}
      </div>
    );
  }

  // Boolean
  if (column?.data_type === "boolean") {
    return <span>{value ? "Yes" : "No"}</span>;
  }

  // Date. An organization's own `date` column holds a day it stated, so it
  // renders verbatim -- the same rule, and the same module, the RMS grid cell
  // for this attribute applies.
  if (column?.data_type === "date") {
    return (
      <span>
        {isTemporal(value)
          ? formatTemporal(value, "medium")
          : formatDay(String(value))}
      </span>
    );
  }

  // Percentage
  if (column?.data_type === "percentage") {
    return <span>{Number(value).toFixed(2)}%</span>;
  }

  // Array of scalars
  if (Array.isArray(value)) {
    return <span>{value.join(", ")}</span>;
  }

  return <span>{String(value)}</span>;
}

function MetadataContent({
  data,
  columns,
  onOpenVersion,
}: {
  data: DocumentMetadata;
  columns: DocumentColumn[];
  onOpenVersion?: () => void;
}) {
  const columnsById = new Map(columns.map((c) => [String(c.id), c]));
  // The zone an upload instant is read in (#11055).
  const viewerZone = useViewerTimeZone();

  // #10600: the extracted "Fund Name", "Fund Managers", and
  // "Organization" fields duplicate the RMS links when those are present, and
  // the overlap reads as a confusing/contradictory "duplicate" field. Prefer
  // the curated RMS link — hide each extracted field once a link of the
  // matching kind exists on the document.
  //
  // #11238 extends that to People / Companies / Topics. Now that links render
  // as their own rows, a linked `person` produces a "People" row, which is the
  // label the extracted field already uses — so without this the panel would
  // show two rows under one label.
  const linkedKinds = new Set(data.linked_entities.map((e) => e.kind));
  const hasLinkedVehicle = linkedKinds.has("vehicle");
  const hasLinkedManager = linkedKinds.has("manager");
  const hasLinkedCompany = linkedKinds.has("company");
  const hasLinkedPerson = linkedKinds.has("person");
  const hasLinkedTopic = linkedKinds.has("topic");

  // The record's fund names (#13708): a multi-fund record with no fund
  // name shows the funds it lists.
  const fundNames =
    data.fund_names ??
    (data.fund_attributes?.fund_name ? [data.fund_attributes.fund_name] : []);
  const showFundName = fundNames.length > 0 && !hasLinkedVehicle;
  const showStrategy = Boolean(data.fund_attributes?.fund_strategy_label);
  const showFundManagers =
    Boolean(data.fund_attributes?.fund_managers?.length) && !hasLinkedManager;
  const showOrganization =
    Boolean(data.organization?.org_name) &&
    !(hasLinkedManager || hasLinkedCompany);
  const showPublisher = Boolean(data.publisher?.organization_name);
  const showPeople =
    Boolean(data.people && data.people.length > 0) && !hasLinkedPerson;
  const showCompanies =
    Boolean(data.companies && data.companies.length > 0) && !hasLinkedCompany;
  const showTopics =
    Boolean(data.topics && data.topics.length > 0) && !hasLinkedTopic;
  const showGeography = Boolean(
    data.geographical && data.geographical.length > 0
  );

  const linkedByKind = new Map<string, LinkedEntity[]>();
  for (const entity of data.linked_entities) {
    const group = linkedByKind.get(entity.kind);
    if (group) group.push(entity);
    else linkedByKind.set(entity.kind, [entity]);
  }
  const linkedKindsInOrder = [
    ...linkedKindOrder.filter((kind) => linkedByKind.has(kind)),
    ...[...linkedByKind.keys()].filter(
      (kind) => !linkedKindOrder.includes(kind)
    ),
  ];

  const summary = data.summary ? trimSummary(data.summary) : null;

  // #11238: one flat property list. The panel used to split these under
  // "Document Info" / "Extracted Metadata" / "Attributes", which grouped them
  // by the subsystem that produced each field — the blob row, the extraction
  // pass, the org's own columns. A reader opening Details wants the document's
  // properties, not our provenance, so the headers are gone.
  //
  // `financial_time_period` is deliberately not rendered: it restates the
  // document date at best and contradicts it at worst, and its period type is
  // a free-text internal artifact rather than something a reader asked for.
  //
  // The summary leads: it is what the reader is after, and everything below it
  // is reference detail. A hairline rule is all that separates the two.
  return (
    <div className="space-y-1.5">
      {summary && (
        <div className="border-b border-gray-100 pb-2 flex items-start gap-1.5">
          <Sparkle className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{summary}</p>
        </div>
      )}

      {/* The document's other copies and revisions (#11956) sit above the
          property list: whether this is the latest version, and where the
          others live, is what a reader with several of them wants first. */}
      {data.lineage && data.lineage.member_count > 1 && (
        <DocumentVersionsSection
          lineage={data.lineage}
          onOpenVersion={onOpenVersion}
        />
      )}

      <dl className="space-y-1.5">
        <MetadataField label="Title" value={data.document_title} />
        <MetadataField
          label="Type"
          value={formatDocumentTypes(data.document_types)}
        />
        <MetadataField
          label="Document Date"
          value={formatDay(data.document_date)}
        />
        <MetadataField
          label="Effective Date"
          value={formatDay(data.effective_date)}
        />
        <MetadataField
          label="Uploaded"
          value={formatUploadedAt(data.uploaded_at, viewerZone)}
        />
        <MetadataField label="Uploaded By" value={data.uploaded_by_email} />
        {data.source_emails.length > 0 && (
          <MetadataField
            label="Source Email"
            value={
              <SourceEmailValue
                emails={data.source_emails}
                onOpen={onOpenVersion}
              />
            }
          />
        )}
        <MetadataField
          label="Path"
          value={
            data.filesystem_path && data.filesystem_path.length > 0
              ? data.filesystem_path.join(" / ")
              : null
          }
        />
        {linkedKindsInOrder.map((kind) => {
          const entities = linkedByKind.get(kind) ?? [];
          return (
            <MetadataField
              key={kind}
              label={kindLabel(kind, entities.length)}
              value={<LinkedRecordValue entities={entities} />}
            />
          );
        })}
        {showFundName && (
          <MetadataField label="Fund Name" value={fundNames.join("; ")} />
        )}
        {showStrategy && (
          <MetadataField
            label="Strategy"
            value={data.fund_attributes?.fund_strategy_label}
          />
        )}
        {showFundManagers && (
          <MetadataField
            label="Fund Managers"
            value={data.fund_attributes?.fund_managers?.join(", ")}
          />
        )}
        {showOrganization && (
          <MetadataField
            label="Organization"
            value={data.organization?.org_name}
          />
        )}
        {showPublisher && (
          <MetadataField
            label="Publisher"
            value={data.publisher?.organization_name}
          />
        )}
        {showPeople && (
          <MetadataField
            label="People"
            value={data.people
              ?.map(
                (p) =>
                  [p.person_name, p.person_role].filter(Boolean).join(" — ") ||
                  "Unknown"
              )
              .join(", ")}
          />
        )}
        {showCompanies && (
          <MetadataField
            label="Companies"
            value={data.companies
              ?.map((c) => c.company_name)
              .filter(Boolean)
              .join(", ")}
          />
        )}
        {showTopics && (
          <MetadataField label="Topics" value={data.topics?.join(", ")} />
        )}
        {showGeography && (
          <MetadataField
            label="Geography"
            value={data.geographical
              ?.map((g) => g.geo_name)
              .filter(Boolean)
              .join(", ")}
          />
        )}
        {Object.entries(data.custom_attributes).map(([colId, value]) => {
          const column = columnsById.get(colId);
          if (!column || value === null || value === undefined) return null;
          return (
            <MetadataField
              key={colId}
              label={column.name}
              value={<CustomAttributeValue value={value} column={column} />}
            />
          );
        })}
      </dl>
    </div>
  );
}

interface DocumentMetadataPanelProps {
  blobId: string | null;
  onClose: () => void;
  // Called after another document is opened from the panel (a version from
  // the Versions section, an email from Source Email); the host decides what
  // that means for the panel (at phone width, closing it).
  onOpenVersion?: () => void;
}

const DocumentMetadataPanel: React.FC<DocumentMetadataPanelProps> = ({
  blobId,
  onClose,
  onOpenVersion,
}) => {
  const {
    data: metadata,
    isLoading,
    error,
  } = useDocumentMetadata(blobId, true);

  const { data: columns } = useDocumentColumns();

  return (
    <div className="h-full flex flex-col border-l border-gray-200 bg-white">
      {/* Header */}
      <div className="h-12 shrink-0 border-b border-gray-200 flex items-center justify-between px-3">
        <span className="text-sm font-semibold text-gray-700">Details</span>
        <Tooltip title="Close details" arrow placement="bottom">
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded cursor-pointer transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </Tooltip>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <CircleNotch className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        )}
        {error && (
          <p className="text-sm text-red-600">Failed to load metadata.</p>
        )}
        {metadata && (
          <MetadataContent
            data={metadata}
            columns={columns ?? []}
            onOpenVersion={onOpenVersion}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentMetadataPanel;
